import * as z from "zod";
import { readDataFromPath } from "./field-data";
import type { AnySchema } from "./field-rule";
import type { FormState, GenericValues } from "./form-context";
import type { Path } from "./types";

export type ValidatorApi<TValues extends GenericValues> = {
	validate<TPath extends Path<TValues>>(path: TPath, schema: AnySchema): string | null;
	validateAsync<TPath extends Path<TValues>>(
		path: TPath,
		schema: AnySchema
	): Promise<string | null>;
};

export function useValidator<TValues extends GenericValues>(
	state: FormState<TValues>
): ValidatorApi<TValues> {
	const { values } = state;

	function processResult(result: z.SafeParseReturnType<any, any>): string | null {
		if (result.success) {
			return null;
		} else {
			const issue = result.error.issues.at(0);
			if (issue == null) {
				return null;
			}

			return (
				new DOMParser().parseFromString(issue.message, "text/html").documentElement.textContent ||
				""
			);
		}
	}

	function getValue<TPath extends Path<TValues>>(path: TPath) {
		const value = path ? readDataFromPath(values, path) : values;

		// Force undefined for null value, since Number(null) === 0 and Number(undefined) === NaN
		return value == null ? undefined : value;
	}

	function testSchema<TPath extends Path<TValues>>(
		path: TPath,
		schema: z.ZodTypeAny
	): string | null {
		const value = getValue(path);

		const result = schema.safeParse(value);

		return processResult(result);
	}

	async function testSchemaAsync<TPath extends Path<TValues>>(
		path: TPath,
		schema: z.ZodTypeAny
	): Promise<string | null> {
		const value = getValue(path);

		const result = await schema.safeParseAsync(value);

		return processResult(result);
	}

	return {
		validate(path, schema) {
			const schemaErrors = testSchema(path, schema);

			return schemaErrors;
		},

		async validateAsync(path, schema) {
			const schemaErrors = await testSchemaAsync(path, schema);

			return schemaErrors;
		},
	};
}
